import { actionPriorities } from '@/domain/actions/actions.interface';
import { UniverseService } from '@/domain/universes/universe.service';
import { UsagesService } from '@/domain/usages/usages.service';
import { ObservableInputsManager } from '@/shared/utils/angular-sdk';
import { ReferenceListName } from '@/utils/reference-lists/reference-lists.interface';
import { ReferenceListsService } from '@/utils/reference-lists/reference-lists.service';
import { isNotNullish } from '@/utils/typing/is-nullish.predicate';
import { hasOwnProperty } from '@/utils/typing/properties.type';
import { AsyncPipe } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    inject,
    Input,
    type OnChanges,
    type OnInit,
    Output,
    type SimpleChanges
} from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import { combineLatest, type Observable, of, ReplaySubject } from 'rxjs';
import { filter, map, switchMap, tap } from 'rxjs/operators';
import {
    BusinessIntelligenceContextualPeriodCode,
    type BusinessIntelligenceFilter,
    BusinessIntelligenceFilterOperator,
    type BusinessIntelligenceMember,
    type BusinessIntelligenceMemberCode,
    type BusinessIntelligencePeriodCode,
    BusinessIntelligenceStaticPeriodCode
} from '../../../interfaces/BusinessIntelligence.interface';
import { FormattableType, type RatioValueWithUnitTypeParams } from '../../../interfaces/Formatter.interface';
import { BuildingsProvider } from '../../../providers/buildings/buildings.provider';
import { BusinessIntelligenceProvider } from '../../../providers/business-intelligence/business-intelligence.provider';
import { API_DATE_FORMAT } from '../../../providers/constants.api';
import { TimeseriesTagProvider } from '../../../providers/tag/timeseries-tag.provider';
import { type Filter, FilterEditorComponent, type FilterField, FilterOperator, FilterTypeCode } from '../../filter-editor/filter-editor';

@UntilDestroy()
@Component({
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ['./business-intelligence-filter-editor.scss'],
    selector: 'business-intelligence-filter-editor',
    templateUrl: 'business-intelligence-filter-editor.html',
    imports: [FilterEditorComponent, TranslatePipe, AsyncPipe]
})
export class BusinessIntelligenceFilterEditorComponent implements OnChanges, OnInit {
    private _translateService = inject(TranslateService);
    private _referenceListsService = inject(ReferenceListsService);
    private _universeService = inject(UniverseService);
    private _buildingsProvider = inject(BuildingsProvider);
    private _businessIntelligenceProvider = inject(BusinessIntelligenceProvider);
    private _timeseriesTagProvider = inject(TimeseriesTagProvider);
    private _usagesService = inject(UsagesService);

    private _observableInputsManager = new ObservableInputsManager<BusinessIntelligenceFilterEditorComponent>(this);
    @Input() availableMembers!: BusinessIntelligenceMember[];
    availableMembers$ = this._observableInputsManager.observe('availableMembers');
    @Input() filter!: BusinessIntelligenceFilter;
    filter$ = this._observableInputsManager.observe('filter');
    @Output() filterChanged: EventEmitter<BusinessIntelligenceFilter> = new EventEmitter();

    fields$!: Observable<FilterField[]>;
    adapterFilter!: Filter;
    newFilter$: ReplaySubject<Filter> = new ReplaySubject(1);

    ngOnChanges(changes: SimpleChanges): void {
        this._observableInputsManager.triggerChanges(changes);
    }

    ngOnInit() {
        this.availableMembers$
            .pipe(
                filter(isNotNullish),
                tap(
                    availableMembers =>
                        (this.fields$ = combineLatest(
                            availableMembers.map(member => this.transformBusinessIntelligenceMemberIntoFilterField$(member))
                        ))
                ),
                switchMap(() => this.filter$),
                filter(isNotNullish),
                untilDestroyed(this)
            )
            .subscribe(
                filter =>
                    (this.adapterFilter = BusinessIntelligenceFilterEditorComponent.transformBusinessIntelligenceFilterIntoFilter(filter)!)
            );

        this.newFilter$
            .pipe(
                filter(isNotNullish),
                switchMap(filter => this.transformFilterIntoBusinessIntelligenceFilter$(filter, this.availableMembers)),
                untilDestroyed(this)
            )
            .subscribe(businessIntelligenceFilter => {
                this.filterChanged.emit(businessIntelligenceFilter ?? undefined);
            });
    }

    onFilterChanged(filter: Filter) {
        this.newFilter$.next(filter);
    }

    private transformBusinessIntelligenceMemberIntoFilterField$(
        businessIntelligenceMember: BusinessIntelligenceMember
    ): Observable<FilterField> {
        const field$ = this._referenceListsService
            .getReferenceListItemLabel$(ReferenceListName.RESOURCE_TYPE, businessIntelligenceMember.resourceTypeCode!)
            .pipe(
                map(label => ({
                    code: businessIntelligenceMember.memberCode,
                    label: businessIntelligenceMember.name,
                    groupLabel: label
                }))
            );

        let filterField$: Observable<FilterField>;
        switch (businessIntelligenceMember.typeConfig.type) {
            case FormattableType.DATE:
            case FormattableType.DATETIME:
                filterField$ = field$.pipe(
                    map(field => ({
                        ...field,
                        type: {
                            code: FilterTypeCode.DATE,
                            options: [
                                ...Object.values(BusinessIntelligenceContextualPeriodCode),
                                ...Object.values(BusinessIntelligenceStaticPeriodCode)
                            ].map(period => {
                                const isLastYear = period.endsWith('_LAST_YEAR');
                                return {
                                    code: period,
                                    label: isLastYear
                                        ? this._translateService.instant('PERIODS.' + period.replace('_LAST_YEAR', '')) +
                                          ' ' +
                                          this._translateService.instant('PERIODS.LAST_YEAR_CODE')
                                        : this._translateService.instant('PERIODS.' + period),
                                    groupLabel: isLastYear
                                        ? this._translateService.instant('PERIODS.LAST_YEAR')
                                        : this._translateService.instant('PERIODS.CURRENT_YEAR')
                                };
                            })
                        }
                    }))
                );
                break;
            case FormattableType.MONTH_DATE:
                filterField$ = field$.pipe(
                    map(field => ({
                        ...field,
                        type: {
                            code: FilterTypeCode.DATE,
                            options: [
                                ...Object.values(BusinessIntelligenceContextualPeriodCode),
                                BusinessIntelligenceStaticPeriodCode.THIS_MONTH,
                                BusinessIntelligenceStaticPeriodCode.LAST_MONTH,
                                BusinessIntelligenceStaticPeriodCode.THIS_QUARTER,
                                BusinessIntelligenceStaticPeriodCode.LAST_QUARTER,
                                BusinessIntelligenceStaticPeriodCode.THIS_HALF,
                                BusinessIntelligenceStaticPeriodCode.LAST_HALF,
                                BusinessIntelligenceStaticPeriodCode.LAST_12_MONTHS,
                                BusinessIntelligenceStaticPeriodCode.THIS_YEAR,
                                BusinessIntelligenceStaticPeriodCode.LAST_YEAR,
                                BusinessIntelligenceStaticPeriodCode.ALL,
                                BusinessIntelligenceStaticPeriodCode.THIS_MONTH_LAST_YEAR,
                                BusinessIntelligenceStaticPeriodCode.LAST_MONTH_LAST_YEAR,
                                BusinessIntelligenceStaticPeriodCode.THIS_HALF_LAST_YEAR,
                                BusinessIntelligenceStaticPeriodCode.LAST_HALF_LAST_YEAR,
                                BusinessIntelligenceStaticPeriodCode.THIS_QUARTER_LAST_YEAR,
                                BusinessIntelligenceStaticPeriodCode.LAST_QUARTER_LAST_YEAR
                            ].map(period => {
                                const isLastYear = period.endsWith('_LAST_YEAR');
                                return {
                                    code: period,
                                    label: isLastYear
                                        ? this._translateService.instant('PERIODS.' + period.replace('_LAST_YEAR', '')) +
                                          ' ' +
                                          this._translateService.instant('PERIODS.LAST_YEAR_CODE')
                                        : this._translateService.instant('PERIODS.' + period),
                                    groupLabel: isLastYear
                                        ? this._translateService.instant('PERIODS.LAST_YEAR')
                                        : this._translateService.instant('PERIODS.CURRENT_YEAR')
                                };
                            })
                        }
                    }))
                );
                break;
            case FormattableType.UNIVERSE_ID:
                filterField$ = field$.pipe(
                    switchMap(field => {
                        return this._universeService.getAllUniverses$().pipe(
                            switchMap(allUniverse =>
                                combineLatest(
                                    allUniverse.map(universe =>
                                        this._referenceListsService.getReferenceListItem$(ReferenceListName.UNIVERSE, universe.name).pipe(
                                            filter(isNotNullish),
                                            map(referenceUniverse => ({
                                                code: referenceUniverse.code,
                                                label: referenceUniverse.label
                                            }))
                                        )
                                    )
                                ).pipe(
                                    map(options => ({
                                        ...field,
                                        type: {
                                            code: FilterTypeCode.SINGLE_SELECTION,
                                            options
                                        }
                                    }))
                                )
                            )
                        );
                    })
                );
                break;
            case FormattableType.BUILDING_TYPE:
                filterField$ = field$.pipe(
                    switchMap(field => {
                        return this._buildingsProvider.getAvailableBuildingTypes$().pipe(
                            switchMap(buildingTypes =>
                                combineLatest(
                                    buildingTypes.map(buildingType =>
                                        this._referenceListsService.getReferenceListItem$(ReferenceListName.BUILDING, buildingType).pipe(
                                            map(referenceBuilding =>
                                                referenceBuilding
                                                    ? {
                                                          code: referenceBuilding.code,
                                                          label: referenceBuilding.label
                                                      }
                                                    : { code: buildingType, label: buildingType }
                                            )
                                        )
                                    )
                                ).pipe(
                                    map(options => ({
                                        ...field,
                                        type: { code: FilterTypeCode.SINGLE_SELECTION, options }
                                    }))
                                )
                            )
                        );
                    })
                );
                break;
            case FormattableType.ACTION_TYPE:
                filterField$ = field$.pipe(
                    switchMap(field => {
                        return this._referenceListsService
                            .getReferenceList$(ReferenceListName.ACTION_TYPE)
                            .pipe(
                                map(referenceList =>
                                    referenceList.map(referenceListItem => ({
                                        code: referenceListItem.code,
                                        label: referenceListItem.label
                                    }))
                                )
                            )
                            .pipe(
                                map(options => ({
                                    ...field,
                                    type: { code: FilterTypeCode.SINGLE_SELECTION, options }
                                }))
                            );
                    })
                );
                break;
            case FormattableType.ACTION_STATUS:
                filterField$ = field$.pipe(
                    switchMap(field => {
                        return this._referenceListsService
                            .getReferenceList$(ReferenceListName.ACTION_STATUS)
                            .pipe(
                                map(referenceList =>
                                    referenceList.map(referenceListItem => ({
                                        code: referenceListItem.code,
                                        label: referenceListItem.label
                                    }))
                                )
                            )
                            .pipe(
                                map(options => ({
                                    ...field,
                                    type: { code: FilterTypeCode.SINGLE_SELECTION, options }
                                }))
                            );
                    })
                );
                break;
            case FormattableType.USAGE_CODE:
                filterField$ = field$.pipe(
                    switchMap(field => {
                        return this._usagesService.getUsages$().pipe(
                            map(options => ({
                                ...field,
                                type: { code: FilterTypeCode.MULTIPLE_SELECTION, options }
                            }))
                        );
                    })
                );
                break;
            case FormattableType.ACTION_PRIORITY:
                filterField$ = field$.pipe(
                    map(field => {
                        return {
                            ...field,
                            type: {
                                code: FilterTypeCode.SINGLE_SELECTION,
                                options: actionPriorities.map(priorityLevel => ({
                                    code: priorityLevel.toString(),
                                    label: this._translateService.instant('ACTIONS.LIST_VALUES.LEVEL.' + priorityLevel)
                                }))
                            }
                        };
                    })
                );
                break;
            case FormattableType.RATIO_VALUE_WITH_UNIT:
                return field$.pipe(
                    map(
                        field =>
                            ({
                                ...field,
                                type: {
                                    code: FilterTypeCode.NUMBER,
                                    suffix:
                                        isNotNullish(businessIntelligenceMember) &&
                                        isNotNullish(businessIntelligenceMember.typeConfig) &&
                                        isNotNullish(businessIntelligenceMember.typeConfig.typeParams) &&
                                        isNotNullish(
                                            (businessIntelligenceMember.typeConfig.typeParams as RatioValueWithUnitTypeParams)
                                                .denominatorUnitCode
                                        ) &&
                                        isNotNullish(
                                            (businessIntelligenceMember.typeConfig.typeParams as RatioValueWithUnitTypeParams)
                                                .numeratorUnitCode
                                        )
                                            ? this._referenceListsService.getReferenceListItemR(
                                                  ReferenceListName.UNIT,
                                                  (businessIntelligenceMember.typeConfig.typeParams as RatioValueWithUnitTypeParams)
                                                      .numeratorUnitCode
                                              ).label +
                                              ' / ' +
                                              this._referenceListsService.getReferenceListItemR(
                                                  ReferenceListName.UNIT,
                                                  (
                                                      businessIntelligenceMember.typeConfig.typeParams as RatioValueWithUnitTypeParams
                                                  ).denominatorUnitCode.toString()
                                              ).label
                                            : null
                                }
                            }) as FilterField
                    )
                );

            case FormattableType.VALUE_WITH_UNIT:
                const unit = businessIntelligenceMember.typeConfig.typeParams.unitCode ?? null;
                return field$.pipe(
                    map(
                        field =>
                            ({
                                ...field,
                                type: {
                                    code: FilterTypeCode.NUMBER,
                                    suffix: unit
                                        ? this._referenceListsService.getReferenceListItemR(ReferenceListName.UNIT, unit).label
                                        : undefined
                                }
                            }) as FilterField
                    )
                );
            case FormattableType.OCCUPANCY_TYPE:
                filterField$ = field$.pipe(
                    switchMap(field => {
                        return this._referenceListsService
                            .getReferenceList$(ReferenceListName.OCCUPANCY_TYPE)
                            .pipe(
                                map(referenceList =>
                                    referenceList.map(referenceListItem => ({
                                        code: referenceListItem.code,
                                        label: referenceListItem.label
                                    }))
                                )
                            )
                            .pipe(
                                map(options => ({
                                    ...field,
                                    type: { code: FilterTypeCode.SINGLE_SELECTION, options }
                                }))
                            );
                    })
                );
                break;
            case FormattableType.TAG_NAME:
                filterField$ = field$.pipe(
                    switchMap(field => {
                        return this._timeseriesTagProvider
                            .getAllTimeseriesTags$()
                            .pipe(
                                map(timeseriesTags =>
                                    timeseriesTags.map(timeseriesTag => ({
                                        code: timeseriesTag.name,
                                        label: timeseriesTag.name
                                    }))
                                )
                            )
                            .pipe(
                                map(options => ({
                                    ...field,
                                    type: { code: FilterTypeCode.SINGLE_SELECTION, options }
                                }))
                            );
                    })
                );
                break;
            case FormattableType.TAG_ID:
                filterField$ = field$.pipe(
                    switchMap(field => {
                        return this._timeseriesTagProvider
                            .getAllTimeseriesTags$()
                            .pipe(
                                map(timeseriesTags =>
                                    timeseriesTags.map(timeseriesTag => ({
                                        code: String(timeseriesTag.tagId),
                                        label: timeseriesTag.name
                                    }))
                                )
                            )
                            .pipe(
                                map(options => ({
                                    ...field,
                                    type: { code: FilterTypeCode.SINGLE_SELECTION, options }
                                }))
                            );
                    })
                );
                break;
            case FormattableType.DATA_SOURCE:
                filterField$ = field$.pipe(
                    switchMap(field => {
                        return this._referenceListsService
                            .getReferenceList$(ReferenceListName.POINT_SEN_DATA_SOURCE)
                            .pipe(
                                map(referenceList =>
                                    referenceList.map(referenceListItem => ({
                                        code: referenceListItem.code,
                                        label: referenceListItem.label
                                    }))
                                )
                            )
                            .pipe(
                                map(options => ({
                                    ...field,
                                    type: { code: FilterTypeCode.SINGLE_SELECTION, options }
                                }))
                            );
                    })
                );
                break;
            case FormattableType.STRING:
                filterField$ = field$.pipe(
                    switchMap(field =>
                        this._businessIntelligenceProvider
                            .notStandardFilterFieldTypeAdapter$(businessIntelligenceMember)
                            .pipe(map(notStandardtype => ({ ...field, type: { ...notStandardtype } })))
                    )
                );
                break;
            case FormattableType.NUMBER:
                filterField$ = field$.pipe(
                    map(field => ({
                        ...field,
                        type: { code: FilterTypeCode.NUMBER }
                    }))
                );
                break;
            case FormattableType.BOOLEAN:
                filterField$ = field$.pipe(
                    map(field => ({
                        ...field,
                        type: { code: FilterTypeCode.BOOLEAN }
                    }))
                );

                break;
            default:
                filterField$ = field$.pipe(
                    map(field => ({
                        ...field,
                        type: {
                            code: FilterTypeCode.STRING
                        }
                    }))
                );

                break;
        }
        return filterField$.pipe(
            map(field => {
                if (field.type.code === FilterTypeCode.SINGLE_SELECTION) {
                    return {
                        ...field,
                        type: {
                            code:
                                businessIntelligenceMember.isMultipleSelectionAllowed === false
                                    ? FilterTypeCode.STRICT_SINGLE_SELECTION
                                    : FilterTypeCode.SINGLE_SELECTION,
                            options: field.type.options.filter(
                                opt =>
                                    businessIntelligenceMember.typeConfig.restrictedList === undefined ||
                                    businessIntelligenceMember.typeConfig.restrictedList.includes(opt.code)
                            )
                        }
                    };
                } else if (field.type.code === FilterTypeCode.MULTIPLE_SELECTION) {
                    return {
                        ...field,
                        type: {
                            code: FilterTypeCode.MULTIPLE_SELECTION,
                            options: field.type.options.filter(
                                opt =>
                                    businessIntelligenceMember.typeConfig.restrictedList === undefined ||
                                    businessIntelligenceMember.typeConfig.restrictedList.includes(opt.code)
                            )
                        }
                    };
                }
                return field;
            })
        );
    }

    private static transformBusinessIntelligenceFilterIntoFilter(filter: BusinessIntelligenceFilter): Filter | null {
        if (!filter) {
            return null;
        }

        const operator = filter.operator;
        switch (filter.operator) {
            case BusinessIntelligenceFilterOperator.CONTAINS:
                return {
                    fieldCode: filter.memberCode,
                    filterOperator: FilterOperator.CONTAINS,
                    operatorParams: filter.operatorParams,
                    isValid: true
                };
            case BusinessIntelligenceFilterOperator.DOES_NOT_CONTAIN:
                return {
                    fieldCode: filter.memberCode,
                    filterOperator: FilterOperator.DOES_NOT_CONTAIN,
                    operatorParams: filter.operatorParams,
                    isValid: true
                };
            case BusinessIntelligenceFilterOperator.IS:
                return {
                    fieldCode: filter.memberCode,
                    filterOperator: FilterOperator.IS,
                    operatorParams: filter.operatorParams,
                    isValid: true
                };
            case BusinessIntelligenceFilterOperator.IS_IN:
                return {
                    fieldCode: filter.memberCode,
                    filterOperator: FilterOperator.IS_IN,
                    operatorParams: filter.operatorParams,
                    isValid: true
                };
            case BusinessIntelligenceFilterOperator.IS_NOT:
                return {
                    fieldCode: filter.memberCode,
                    filterOperator: FilterOperator.IS_NOT,
                    operatorParams: filter.operatorParams,
                    isValid: true
                };
            case BusinessIntelligenceFilterOperator.IS_NOT_IN:
                return {
                    fieldCode: filter.memberCode,
                    filterOperator: FilterOperator.IS_NOT_IN,
                    operatorParams: filter.operatorParams,
                    isValid: true
                };
            case BusinessIntelligenceFilterOperator.IS_EMPTY:
                return {
                    fieldCode: filter.memberCode,
                    filterOperator: FilterOperator.IS_EMPTY,
                    operatorParams: filter.operatorParams,
                    isValid: true
                };
            case BusinessIntelligenceFilterOperator.IS_NOT_EMPTY:
                return {
                    fieldCode: filter.memberCode,
                    filterOperator: FilterOperator.IS_NOT_EMPTY,
                    operatorParams: filter.operatorParams,
                    isValid: true
                };
            case BusinessIntelligenceFilterOperator.IS_TRUE: {
                return {
                    fieldCode: filter.memberCode,
                    filterOperator: FilterOperator.IS_TRUE,
                    operatorParams: {},
                    isValid: true
                };
            }
            case BusinessIntelligenceFilterOperator.IS_FALSE: {
                return {
                    fieldCode: filter.memberCode,
                    filterOperator: FilterOperator.IS_FALSE,
                    operatorParams: {},
                    isValid: true
                };
            }
            case BusinessIntelligenceFilterOperator.IS_GREATER_THAN:
                return {
                    fieldCode: filter.memberCode,
                    filterOperator: FilterOperator.IS_GREATER_THAN,
                    operatorParams: filter.operatorParams,
                    isValid: true
                };
            case BusinessIntelligenceFilterOperator.IS_GREATER_THAN_OR_EQUAL:
                return {
                    fieldCode: filter.memberCode,
                    filterOperator: FilterOperator.IS_GREATER_THAN_OR_EQUAL,
                    operatorParams: filter.operatorParams,
                    isValid: true
                };
            case BusinessIntelligenceFilterOperator.IS_SMALLER_THAN:
                return {
                    fieldCode: filter.memberCode,
                    filterOperator: FilterOperator.IS_SMALLER_THAN,
                    operatorParams: filter.operatorParams,
                    isValid: true
                };
            case BusinessIntelligenceFilterOperator.IS_SMALLER_THAN_OR_EQUAL:
                return {
                    fieldCode: filter.memberCode,
                    filterOperator: FilterOperator.IS_SMALLER_THAN_OR_EQUAL,
                    operatorParams: filter.operatorParams,
                    isValid: true
                };
            case BusinessIntelligenceFilterOperator.BETWEEN:
                return {
                    fieldCode: filter.memberCode,
                    filterOperator: FilterOperator.BETWEEN,
                    operatorParams: { firstValue: filter.operatorParams.min, secondValue: filter.operatorParams.max },
                    isValid: true
                };
            case BusinessIntelligenceFilterOperator.IN_DATE_RANGE:
                return {
                    fieldCode: filter.memberCode,
                    filterOperator: FilterOperator.IN_DATE_RANGE,
                    operatorParams: { firstValue: filter.operatorParams.start, secondValue: filter.operatorParams.end },
                    isValid: true
                };
            case BusinessIntelligenceFilterOperator.IS_PERIOD:
                return {
                    fieldCode: filter.memberCode,
                    filterOperator: FilterOperator.IS,
                    operatorParams: { value: filter.operatorParams.period },
                    isValid: true
                };
            case BusinessIntelligenceFilterOperator.DATE_FROM:
                return {
                    fieldCode: filter.memberCode,
                    filterOperator: FilterOperator.DATE_FROM,
                    operatorParams: filter.operatorParams,
                    isValid: true
                };
            case BusinessIntelligenceFilterOperator.MATCHES_ALL:
                return {
                    fieldCode: filter.memberCode,
                    filterOperator: FilterOperator.MATCHES_ALL,
                    operatorParams: filter.operatorParams,
                    isValid: true
                };
            case BusinessIntelligenceFilterOperator.MATCHES_ANY:
                return {
                    fieldCode: filter.memberCode,
                    filterOperator: FilterOperator.MATCHES_ANY,
                    operatorParams: filter.operatorParams,
                    isValid: true
                };
            case BusinessIntelligenceFilterOperator.MATCHES_EXACTLY:
                return {
                    fieldCode: filter.memberCode,
                    filterOperator: FilterOperator.MATCHES_EXACTLY,
                    operatorParams: {
                        value: filter.operatorParams.included
                    },
                    isValid: true
                };
            case BusinessIntelligenceFilterOperator.DOES_NOT_MATCH_ANY:
                return {
                    fieldCode: filter.memberCode,
                    filterOperator: FilterOperator.DOES_NOT_MATCH_ANY,
                    operatorParams: filter.operatorParams,
                    isValid: true
                };
            default:
                throw new Error(`Unknown BusinessIntelligenceFilterOperator ${operator}`);
        }
    }

    private transformFilterIntoBusinessIntelligenceFilter$(
        filter: Filter,
        availableMembers: BusinessIntelligenceMember[]
    ): Observable<BusinessIntelligenceFilter | null> {
        if (!filter || !filter.isValid) {
            return of(null);
        }

        const operator = filter.filterOperator;
        const member = availableMembers.find(member => member.memberCode === filter.fieldCode);

        if (member === undefined) {
            return of(null);
        }

        switch (filter.filterOperator) {
            case FilterOperator.CONTAINS:
                return of({
                    memberCode: filter.fieldCode as BusinessIntelligenceMemberCode,
                    operator: BusinessIntelligenceFilterOperator.CONTAINS,
                    operatorParams: filter.operatorParams
                });
            case FilterOperator.DOES_NOT_CONTAIN:
                return of({
                    memberCode: filter.fieldCode as BusinessIntelligenceMemberCode,
                    operator: BusinessIntelligenceFilterOperator.DOES_NOT_CONTAIN,
                    operatorParams: filter.operatorParams
                });
            case FilterOperator.DATE_FROM:
                return of({
                    memberCode: filter.fieldCode as BusinessIntelligenceMemberCode,
                    operator: BusinessIntelligenceFilterOperator.DATE_FROM,
                    operatorParams: filter.operatorParams
                });
            case FilterOperator.IS:
                if (
                    member.typeConfig.type === FormattableType.DATE ||
                    member.typeConfig.type === FormattableType.DATETIME ||
                    member.typeConfig.type === FormattableType.MONTH_DATE
                ) {
                    return of({
                        memberCode: filter.fieldCode as BusinessIntelligenceMemberCode,
                        operator: BusinessIntelligenceFilterOperator.IS_PERIOD,
                        operatorParams: { period: filter.operatorParams.value as BusinessIntelligencePeriodCode }
                    });
                }
                return of({
                    memberCode: filter.fieldCode as BusinessIntelligenceMemberCode,
                    operator: BusinessIntelligenceFilterOperator.IS,
                    operatorParams: filter.operatorParams
                });
            case FilterOperator.IS_IN:
                return of({
                    memberCode: filter.fieldCode as BusinessIntelligenceMemberCode,
                    operator: BusinessIntelligenceFilterOperator.IS_IN,
                    operatorParams: filter.operatorParams
                });
            case FilterOperator.IS_NOT:
                return of({
                    memberCode: filter.fieldCode as BusinessIntelligenceMemberCode,
                    operator: BusinessIntelligenceFilterOperator.IS_NOT,
                    operatorParams: filter.operatorParams
                });
            case FilterOperator.IS_NOT_IN:
                return of({
                    memberCode: filter.fieldCode as BusinessIntelligenceMemberCode,
                    operator: BusinessIntelligenceFilterOperator.IS_NOT_IN,
                    operatorParams: filter.operatorParams
                });
            case FilterOperator.IS_EMPTY:
                return of({
                    memberCode: filter.fieldCode as BusinessIntelligenceMemberCode,
                    operator: BusinessIntelligenceFilterOperator.IS_EMPTY,
                    operatorParams: filter.operatorParams
                });
            case FilterOperator.IS_NOT_EMPTY:
                return of({
                    memberCode: filter.fieldCode as BusinessIntelligenceMemberCode,
                    operator: BusinessIntelligenceFilterOperator.IS_NOT_EMPTY,
                    operatorParams: filter.operatorParams
                });
            case FilterOperator.IS_TRUE:
                return of({
                    memberCode: filter.fieldCode as BusinessIntelligenceMemberCode,
                    operator: BusinessIntelligenceFilterOperator.IS_TRUE,
                    operatorParams: {}
                });
            case FilterOperator.IS_FALSE:
                return of({
                    memberCode: filter.fieldCode as BusinessIntelligenceMemberCode,
                    operator: BusinessIntelligenceFilterOperator.IS_FALSE,
                    operatorParams: {}
                });
            case FilterOperator.IS_GREATER_THAN:
                return of({
                    memberCode: filter.fieldCode as BusinessIntelligenceMemberCode,
                    operator: BusinessIntelligenceFilterOperator.IS_GREATER_THAN,
                    operatorParams: filter.operatorParams
                });
            case FilterOperator.IS_GREATER_THAN_OR_EQUAL:
                return of({
                    memberCode: filter.fieldCode as BusinessIntelligenceMemberCode,
                    operator: BusinessIntelligenceFilterOperator.IS_GREATER_THAN_OR_EQUAL,
                    operatorParams: filter.operatorParams
                });
            case FilterOperator.IS_SMALLER_THAN:
                return of({
                    memberCode: filter.fieldCode as BusinessIntelligenceMemberCode,
                    operator: BusinessIntelligenceFilterOperator.IS_SMALLER_THAN,
                    operatorParams: filter.operatorParams
                });
            case FilterOperator.IS_SMALLER_THAN_OR_EQUAL:
                return of({
                    memberCode: filter.fieldCode as BusinessIntelligenceMemberCode,
                    operator: BusinessIntelligenceFilterOperator.IS_SMALLER_THAN_OR_EQUAL,
                    operatorParams: filter.operatorParams
                });
            case FilterOperator.BETWEEN:
                return of({
                    memberCode: filter.fieldCode as BusinessIntelligenceMemberCode,
                    operator: BusinessIntelligenceFilterOperator.BETWEEN,
                    operatorParams: { min: Number(filter.operatorParams.firstValue), max: Number(filter.operatorParams.secondValue) }
                });
            case FilterOperator.IN_DATE_RANGE:
                return of({
                    memberCode: filter.fieldCode as BusinessIntelligenceMemberCode,
                    operator: BusinessIntelligenceFilterOperator.IN_DATE_RANGE,
                    operatorParams: {
                        start: moment(String(filter.operatorParams.firstValue)).format(API_DATE_FORMAT),
                        end: moment(String(filter.operatorParams.secondValue)).format(API_DATE_FORMAT)
                    }
                });
            case FilterOperator.MATCHES_ALL:
                return of({
                    memberCode: filter.fieldCode as BusinessIntelligenceMemberCode,
                    operator: BusinessIntelligenceFilterOperator.MATCHES_ALL,
                    operatorParams: filter.operatorParams
                });
            case FilterOperator.MATCHES_ANY:
                return of({
                    memberCode: filter.fieldCode as BusinessIntelligenceMemberCode,
                    operator: BusinessIntelligenceFilterOperator.MATCHES_ANY,
                    operatorParams: filter.operatorParams
                });
            case FilterOperator.DOES_NOT_MATCH_ANY:
                return of({
                    memberCode: filter.fieldCode as BusinessIntelligenceMemberCode,
                    operator: BusinessIntelligenceFilterOperator.DOES_NOT_MATCH_ANY,
                    operatorParams: filter.operatorParams
                });
            case FilterOperator.MATCHES_EXACTLY:
                return this.transformBusinessIntelligenceMemberIntoFilterField$(member).pipe(
                    map(filterField => filterField.type),
                    map(memberFieldType => {
                        if (memberFieldType && hasOwnProperty(memberFieldType, 'options')) {
                            return {
                                memberCode: filter.fieldCode as BusinessIntelligenceMemberCode,
                                operator: BusinessIntelligenceFilterOperator.MATCHES_EXACTLY,
                                operatorParams: {
                                    included: filter.operatorParams.value,
                                    excluded: memberFieldType.options
                                        .map((option: any) => option.code)
                                        .filter((element: any) => !filter.operatorParams.value.includes(element))
                                }
                            };
                        }
                        return {
                            memberCode: filter.fieldCode as BusinessIntelligenceMemberCode,
                            operator: BusinessIntelligenceFilterOperator.MATCHES_EXACTLY,
                            operatorParams: {
                                included: filter.operatorParams.value,
                                excluded: null
                            }
                        };
                    })
                );

            default:
                throw new Error(`Unknown filterOperator ${operator}`);
        }
    }
}
